import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, OnDestroy, signal, ViewChild, ViewChildren, QueryList, TemplateRef, ViewContainerRef, ElementRef, effect, HostListener } from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router, NavigationEnd } from "@angular/router";
import { Onboarding } from "app/modules/admin/onboarding/models/onboarding.model";
import { OnboardingService } from "app/modules/admin/onboarding/services/onboarding.service";
import { UserService } from "app/services/user.service";
import { VideoPlayerComponent } from "app/shared/components/video-player/video-player.component";
import { EmptyStateComponent } from "app/shared/components/empty-state/empty-state.component";
import { filter, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-onboarding-view",
  templateUrl: "./onboarding-view.component.html",
  standalone: true,
  imports: [CommonModule, MatIconModule, VideoPlayerComponent, MatTooltipModule, EmptyStateComponent],
  host: {
    class: "fixed w-0 h-0 pointer-events-none z-[999]",
  },
  styles: [
    `
      ::ng-deep .onboarding-backdrop {
        background-color: rgba(0, 0, 0, 0.6) !important;
        backdrop-filter: blur(4px) !important;
      }

      @media (max-width: 639px) {
        ::ng-deep .onboarding-overlay-panel {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          transform: none !important;
        }
      }

      ::ng-deep {
        --modal-open-dur: 250ms;
        --modal-close-dur: 150ms;
        --modal-scale: 0.96;
        --modal-scale-close: 0.96;
        --modal-ease: cubic-bezier(0.22, 1, 0.36, 1);

        .t-modal {
          transform-origin: center;
          transform: scale(var(--modal-scale));
          opacity: 0;
          pointer-events: none;
          transition:
            width 300ms ease-in-out,
            height 300ms ease-in-out,
            transform var(--modal-open-dur) var(--modal-ease),
            opacity   var(--modal-open-dur) var(--modal-ease);
          will-change: transform, opacity, width, height;
        }
        .t-modal.is-open {
          transform: scale(1);
          opacity: 1;
          pointer-events: auto;
        }
        .t-modal.is-closing {
          transform: scale(var(--modal-scale-close));
          opacity: 0;
          pointer-events: none;
          transition:
            width 300ms ease-in-out,
            height 300ms ease-in-out,
            transform var(--modal-close-dur) var(--modal-ease),
            opacity   var(--modal-close-dur) var(--modal-ease);
        }

        .onboarding-monet-modal {
          background:
            radial-gradient(circle at 16% 10%, rgba(147, 197, 253, 0.7), transparent 32%),
            radial-gradient(circle at 86% 18%, rgba(165, 243, 252, 0.65), transparent 30%),
            radial-gradient(circle at 18% 88%, rgba(153, 246, 228, 0.5), transparent 34%),
            radial-gradient(circle at 82% 86%, rgba(191, 219, 254, 0.6), transparent 30%),
            linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(240, 249, 255, 0.93) 46%, rgba(243, 244, 246, 0.95));
        }

        .dark .onboarding-monet-modal {
          background:
            radial-gradient(circle at 14% 8%, rgba(29, 78, 216, 0.15), transparent 34%),
            radial-gradient(circle at 88% 18%, rgba(8, 145, 178, 0.14), transparent 30%),
            radial-gradient(circle at 16% 90%, rgba(15, 118, 110, 0.1), transparent 36%),
            radial-gradient(circle at 84% 88%, rgba(30, 64, 175, 0.12), transparent 30%),
            linear-gradient(135deg, rgba(15, 23, 42, 0.99), rgba(15, 32, 53, 0.98) 48%, rgba(17, 24, 39, 0.96));
        }

        .t-text-swap {
          display: block;
          transform: translateY(0);
          filter: blur(0);
          opacity: 1;
          transition:
            transform var(--text-swap-dur, 150ms) var(--text-swap-ease, ease-in-out),
            filter    var(--text-swap-dur, 150ms) var(--text-swap-ease, ease-in-out),
            opacity   var(--text-swap-dur, 150ms) var(--text-swap-ease, ease-in-out);
          will-change: transform, filter, opacity;
        }

        .t-text-swap.is-exit {
          transform: translateY(calc(var(--text-swap-translate-y, 4px) * -1));
          filter: blur(var(--text-swap-blur, 2px));
          opacity: 0;
        }

        .t-text-swap.is-enter-start {
          transform: translateY(var(--text-swap-translate-y, 4px));
          filter: blur(var(--text-swap-blur, 2px));
          opacity: 0;
          transition: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .t-modal,
          .t-text-swap { transition: none !important; }
        }
      }
    `,
  ],
})
export class OnboardingViewComponent implements OnInit, OnDestroy {
  private readonly _onboardingService = inject(OnboardingService);
  private readonly _userService = inject(UserService);
  private readonly _router = inject(Router);
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  @ViewChild("onboardingOverlay") onboardingOverlay!: TemplateRef<any>;
  @ViewChild("modalContent") modalContent!: ElementRef;
  @ViewChild("modalFooter") modalFooter!: ElementRef;
  @ViewChildren("stepContainer") stepContainers!: QueryList<ElementRef>;

  public onboarding = this._onboardingService.activeOnboarding;
  public isMobile = signal(window.innerWidth < 640);
  public currentStep = signal(0);
  public displayedTitleStep = signal(0);
  public displayedDescriptionStep = signal(0);
  public displayedButtonStep = signal(0);
  public modalHeight = signal<number | null>(null);
  public showFloatingHelpButton = signal(false);
  public floatingButtonSize = signal('large');
  public matchedOnboarding = signal<Onboarding | null>(null);
  public isOpen = signal(false);
  public isClosing = signal(false);
  public skipCountdown = signal(10);
  public titleSwapState = signal("");
  public descriptionSwapState = signal("");
  public buttonSwapState = signal("");
  
  private _overlayRef: OverlayRef | null = null;
  private _injectedButton: HTMLElement | null = null;
  private _retryTimer: any = null;
  private _skipCountdownTimer: any = null;
  private _textSwapTimers: any[] = [];
  private _mutationObserver: MutationObserver | null = null;
  private _currentUserKey: string | number | null = null;
  private readonly _unsubscribeAll = new Subject<void>();

  constructor() {
    // React to onboarding changes from the service
    effect(() => {
        const active = this.onboarding();
        if (active) {
            this.currentStep.set(0);
            this.displayedTitleStep.set(0);
            this.displayedDescriptionStep.set(0);
            this.displayedButtonStep.set(0);
            this.clearTextSwapTimers();
            this.titleSwapState.set("");
            this.descriptionSwapState.set("");
            this.buttonSwapState.set("");
            if (active.isCompleted || active.isSkipped) {
              this.stopSkipCountdown(false);
              this.skipCountdown.set(0);
            } else {
              this.startSkipCountdown();
            }
            this.openOverlay();
            // Defer cleanup to avoid synchronous signal write warning in effect
            setTimeout(() => this.cleanupHelpButton());
        } else {
            this.stopSkipCountdown();
            this.closeOverlay();
            // Re-evaluate help button when guide is closed/completed
            setTimeout(() => this.checkAndAutoShow(this._router.url));
        }
    });

    // React to user profile loading/changes
    effect(() => {
        const user = this._userService.user();
        if (user) {
            const userKey = user.userId ?? user.id ?? null;
            if (userKey !== this._currentUserKey) {
                this._currentUserKey = userKey;
                this._onboardingService.resetUserOnboardings();
            }
            // Defer check to avoid synchronous signal write warning in effect
            setTimeout(() => this.checkAndAutoShow(this._router.url));
        } else if (this._currentUserKey !== null) {
            this._currentUserKey = null;
            this._onboardingService.resetUserOnboardings();
        }
    });
  }

  @HostListener("window:resize")
  onResize(): void {
    this.isMobile.set(window.innerWidth < 640);
    this.updateModalHeight();
  }

  ngOnInit(): void {
    // Listen to router navigation changes
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: any) => {
        this.checkAndAutoShow(event.urlAfterRedirects);
      });

    // Run initial check if we have a URL
    if (this._router.url) {
        this.checkAndAutoShow(this._router.url);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    this.closeOverlay();
    this.cleanupHelpButton();
  }

  private checkAndAutoShow(url: string): void {
    // Only proceed if user is logged in
    if (!this._userService.user()) {
        return;
    }

    // Fetch onboardings related to this user (role-matched), including completed ones.
    // Auto-show is controlled via `isCompleted` rather than hiding entries.
    this._onboardingService.getPendingOnboardings().pipe(takeUntil(this._unsubscribeAll)).subscribe(userOnboardings => {
      const allList = userOnboardings;
      
      // 1. Find all matches from the full list to determine specificity
      const allMatches = allList.filter(o => {
        const matchExact = this._onboardingService.matchRoute(url, o.route, true);
        const matchPartial = this._onboardingService.matchRoute(url, o.route, false);
        return matchExact || matchPartial;
      });

      if (allMatches.length === 0) {
        const active = this.onboarding();
        if (active) {
            this.close();
        }
        this.setupHelpButton(null);
        return;
      }

      // 2. Sort all matches by specificity (longest route string wins)
      allMatches.sort((a, b) => b.route.length - a.route.length);
      const bestMatch = allMatches[0];

      // 3. Only proceed if the best match is ACTIVE
      if (!bestMatch.isActive) {
        const active = this.onboarding();
        if (active) {
            this.close();
        }
        this.setupHelpButton(null);
        return;
      }

      // Keep track of the matched onboarding
      this.matchedOnboarding.set(bestMatch);

      // 4. Auto-show only when not completed
      if (bestMatch.isCompleted) {
        const active = this.onboarding();
        if (active) {
            this.close();
        }
      } else {
        if (!this.onboarding()) {
            this._onboardingService.activeOnboarding.set(bestMatch);
        }
      }

      // Configure help button position dynamically
      this.setupHelpButton(bestMatch);
    });
  }

  private _getPositionStrategy(position: number) {
    const strategy = this._overlay.position().global();
    if (this.isMobile()) {
      return strategy.centerHorizontally().centerVertically();
    }
    switch (position) {
      case 1: // Top Left
        return strategy.left("24px").top("24px");
      case 2: // Top Right
        return strategy.right("24px").top("24px");
      case 4: // Bottom Left
        return strategy.left("24px").bottom("24px");
      case 5: // Bottom Right
        return strategy.right("24px").bottom("24px");
      case 3: // Center
      default:
        return strategy.centerHorizontally().centerVertically();
    }
  }

  private openOverlay(): void {
    // Give Angular a tick to resolve the TemplateRef if needed
    setTimeout(() => {
      if (this._overlayRef || !this.onboardingOverlay) {
        return;
      }

      const activeOnboarding = this.onboarding();
      const position = activeOnboarding ? activeOnboarding.position : 3;

      this._overlayRef = this._overlay.create({
        hasBackdrop: true,
        backdropClass: "onboarding-backdrop",
        panelClass: "onboarding-overlay-panel",
        positionStrategy: this._getPositionStrategy(position),
        scrollStrategy: this._overlay.scrollStrategies.block(),
      });

      const portal = new TemplatePortal(
        this.onboardingOverlay,
        this._viewContainerRef
      );
      this._overlayRef.attach(portal);

      // Measure height after attachment
      this.updateModalHeight();

      // Trigger the opening animation
      setTimeout(() => {
        this.isOpen.set(true);
        this.isClosing.set(false);
      }, 50);
    });
  }

  public updateModalHeight(): void {
    setTimeout(() => {
      const activeIndex = this.currentStep();
      const containerArray = this.stepContainers?.toArray();
      const activeEl = containerArray?.[activeIndex]?.nativeElement ?? containerArray?.[0]?.nativeElement;
      const footerEl = this.modalFooter?.nativeElement;
      if (activeEl) {
        const activeHeight = activeEl.offsetHeight;
        const footerHeight = footerEl ? footerEl.offsetHeight : 0;
        this.modalHeight.set(activeHeight + footerHeight);
      }
    }, 50);
  }

  private closeOverlay(): void {
    this.stopSkipCountdown();
    this.clearTextSwapTimers();
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
    this.isOpen.set(false);
    this.isClosing.set(false);
  }

  public next(): void {
    const currentOnboarding = this.onboarding();
    if (!currentOnboarding) return;

    if (this.currentStep() < (currentOnboarding.items.length || 0) - 1) {
      this.goToStep(this.currentStep() + 1);
    } else {
      this.completeCurrentOnboarding(false);
    }
  }

  public skip(): void {
    if (this.skipCountdown() > 0) return;

    this.completeCurrentOnboarding(true);
  }

  public prev(): void {
    if (this.currentStep() > 0) {
      this.goToStep(this.currentStep() - 1);
    }
  }

  public close(): void {
    if (this.isClosing()) return;
    this.isOpen.set(false);
    this.isClosing.set(true);
    setTimeout(() => {
      this._onboardingService.activeOnboarding.set(null);
      this.modalHeight.set(null);
      this.isClosing.set(false);
    }, 150);
  }

  private completeCurrentOnboarding(isSkipped: boolean): void {
    const currentOnboarding = this.onboarding();
    if (!currentOnboarding) return;

    this._onboardingService.markAsCompleted(currentOnboarding.id, isSkipped).pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: () => {
        this.close();
      },
      error: () => {
        this.close();
      }
    });
  }

  private goToStep(step: number): void {
    const currentOnboarding = this.onboarding();
    if (!currentOnboarding || step === this.currentStep()) return;

    const maxStep = currentOnboarding.items.length - 1;
    const nextStep = Math.max(0, Math.min(step, maxStep));
    const textSwapDuration = this.getTextSwapDuration();
    const descriptionStagger = 45;

    // Check if the button text actually changes (Next -> Finish or Finish -> Next)
    const currentBtnStep = this.displayedButtonStep();
    const currentBtnText = currentBtnStep === maxStep ? "Finish" : "Next";
    const nextBtnText = nextStep === maxStep ? "Finish" : "Next";
    const buttonTextChanged = currentBtnText !== nextBtnText;

    this.clearTextSwapTimers();
    this.currentStep.set(nextStep);
    this.titleSwapState.set("is-exit");
    if (buttonTextChanged) {
      this.buttonSwapState.set("is-exit");
    } else {
      this.displayedButtonStep.set(nextStep);
    }

    this._textSwapTimers.push(setTimeout(() => {
      this.descriptionSwapState.set("is-exit");
    }, descriptionStagger));

    this._textSwapTimers.push(setTimeout(() => {
      this.displayedTitleStep.set(nextStep);
      if (buttonTextChanged) {
        this.displayedButtonStep.set(nextStep);
        this.buttonSwapState.set("is-enter-start");
      }
      this.titleSwapState.set("is-enter-start");
      this.updateModalHeight();

      requestAnimationFrame(() => {
        this.titleSwapState.set("");
        if (buttonTextChanged) {
          this.buttonSwapState.set("");
        }
      });
    }, textSwapDuration));

    this._textSwapTimers.push(setTimeout(() => {
      this.displayedDescriptionStep.set(nextStep);
      this.descriptionSwapState.set("is-enter-start");

      requestAnimationFrame(() => {
        this.descriptionSwapState.set("");
        this.updateModalHeight();
      });
    }, textSwapDuration + descriptionStagger));

    this.updateModalHeight();
  }

  private getTextSwapDuration(): number {
    const duration = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--text-swap-dur")
    );

    return Number.isFinite(duration) ? duration : 150;
  }

  private clearTextSwapTimers(): void {
    this._textSwapTimers.forEach((timer) => clearTimeout(timer));
    this._textSwapTimers = [];
  }

  private startSkipCountdown(): void {
    this.stopSkipCountdown();
    this.skipCountdown.set(10);

    this._skipCountdownTimer = setInterval(() => {
      const remaining = this.skipCountdown();
      if (remaining <= 1) {
        this.skipCountdown.set(0);
        this.stopSkipCountdown(false);
        return;
      }

      this.skipCountdown.set(remaining - 1);
    }, 1000);
  }

  private stopSkipCountdown(reset = true): void {
    if (this._skipCountdownTimer) {
      clearInterval(this._skipCountdownTimer);
      this._skipCountdownTimer = null;
    }

    if (reset) {
      this.skipCountdown.set(10);
    }
  }

  public replayGuide(): void {
    const matched = this.matchedOnboarding();
    if (matched) {
      this._onboardingService.activeOnboarding.set(matched);
    }
  }

  private cleanupHelpButton(): void {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
    if (this._injectedButton) {
      const parent = this._injectedButton.parentElement;
      if (parent && parent.classList.contains('onboarding-heading-wrapper')) {
        const grandparent = parent.parentElement;
        const heading = parent.querySelector('h2, h3, h4');
        if (grandparent && heading) {
          parent.before(heading);
        }
        parent.remove();
      } else {
        this._injectedButton.remove();
      }
      this._injectedButton = null;
    }
    this.showFloatingHelpButton.set(false);
  }

  private resolveHeaderTargetElement(): { targetElement: HTMLElement | null; isHeadingEl: boolean } {
    // 1. Explicit Anchors First
    const explicitAnchors = [
      '[data-onboarding-anchor="page-header-actions"]',
      '[data-onboarding-anchor="project-header-actions"]',
      '[data-onboarding-anchor="header-actions"]',
      '[data-onboarding-anchor="actions-row"]'
    ];
    for (const selector of explicitAnchors) {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        return { targetElement: el, isHeadingEl: false };
      }
    }

    // 2. Action Containers inside page-header or header
    const headerEl = (document.querySelector('.page-header') || document.querySelector('header')) as HTMLElement;
    if (headerEl) {
      const containerSelectors = [
        '.page-header-actions',
        '.toolbar',
        'div.flex.items-center.gap-3',
        'div.flex.items-center.space-x-3',
        'div.flex.shrink-0.items-center',
        'div.flex.shrink-0'
      ];
      for (const selector of containerSelectors) {
        const el = headerEl.querySelector(selector) as HTMLElement;
        if (el) {
          return { targetElement: el, isHeadingEl: false };
        }
      }

      // Fallback: search for any flex row containing a button in the header
      const flexContainers = Array.from(headerEl.querySelectorAll<HTMLElement>('div.flex, div.inline-flex'));
      for (const flex of flexContainers) {
        if (flex.querySelector('button, a.button, a.btn')) {
          return { targetElement: flex, isHeadingEl: false };
        }
      }
    }

    // 3. Smart Heading Siblings
    let heading: Element | null = null;
    if (headerEl) {
      heading = headerEl.querySelector('h2, h3, h4');
    }
    heading ??= document.querySelector('h2, h3, h4');

    if (heading) {
      return { targetElement: heading as HTMLElement, isHeadingEl: true };
    }

    return { targetElement: null, isHeadingEl: false };
  }

  private resolveSelectorsTargetElement(selectors: string[]): { targetElement: HTMLElement | null; isHeadingEl: boolean } {
    for (const selector of selectors) {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        const isHeadingEl = ['H2', 'H3', 'H4'].includes(el.tagName);
        return { targetElement: el, isHeadingEl };
      }
    }
    return { targetElement: null, isHeadingEl: false };
  }

  private createHelpButton(guide: Onboarding, styleVal: string, orderVal: string, btnSizeVal: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    
    let btnClasses = '';
    let svgSizeClass = '';
    
    if (styleVal === 'icon-only') {
      btnClasses = `onboarding-dynamic-help-btn button-icon ${orderVal === 'prepend' ? 'mr-1' : 'ml-1'} shrink-0 pointer-events-auto`;
      if (btnSizeVal === 'xs') {
        btnClasses += ' button-icon-xs';
        svgSizeClass = 'w-3 h-3';
      } else if (btnSizeVal === 'sm') {
        btnClasses += ' button-icon-sm';
        svgSizeClass = 'w-3.5 h-3.5';
      } else if (btnSizeVal === 'lg') {
        btnClasses += ' button-icon-lg';
        svgSizeClass = 'w-5 h-5';
      } else {
        svgSizeClass = 'w-4 h-4';
      }
      
      btn.className = btnClasses;
      btn.innerHTML = `
        <svg class="${svgSizeClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `;
    } else {
      btnClasses = `onboarding-dynamic-help-btn button-tertiary ${orderVal === 'prepend' ? 'mr-1' : 'ml-1'} shrink-0 pointer-events-auto`;
      if (btnSizeVal === 'xs') {
        btnClasses += ' button-xs';
        svgSizeClass = 'w-3 h-3';
      } else if (btnSizeVal === 'sm') {
        btnClasses += ' button-sm';
        svgSizeClass = 'w-3.5 h-3.5';
      } else if (btnSizeVal === 'lg') {
        btnClasses += ' button-lg';
        svgSizeClass = 'w-5 h-5';
      } else {
        svgSizeClass = 'w-4 h-4';
      }
      
      btn.className = btnClasses;
      btn.innerHTML = `
        <svg class="${svgSizeClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>Help</span>
      `;
    }

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._onboardingService.show(guide.id);
    });

    return btn;
  }

  private injectHelpButton(targetElement: HTMLElement, btn: HTMLButtonElement, isHeadingEl: boolean, orderVal: string): void {
    if (isHeadingEl) {
      const parent = targetElement.parentElement;
      if (parent) {
        const display = window.getComputedStyle(parent).display;
        const isFlex = display === 'flex' || display === 'inline-flex';

        if (isFlex) {
          if (orderVal === 'prepend') {
            targetElement.before(btn);
          } else {
            targetElement.after(btn);
          }
        } else {
          // Heading is block-level, wrap heading in flex row container to align inline
          const wrapper = document.createElement('div');
          wrapper.className = 'onboarding-heading-wrapper flex items-center gap-2 inline-flex';
          
          targetElement.before(wrapper);
          wrapper.append(targetElement);

          if (orderVal === 'prepend') {
            targetElement.before(btn);
          } else {
            wrapper.append(btn);
          }
        }
      } else {
        if (orderVal === 'prepend') {
          targetElement.prepend(btn);
        } else {
          targetElement.append(btn);
        }
      }
    } else {
      if (orderVal === 'prepend') {
        targetElement.prepend(btn);
      } else {
        targetElement.append(btn);
      }
    }
  }

  private setupHelpButton(guide: Onboarding | null): void {
    this.cleanupHelpButton();

    if (!guide) {
      return;
    }

    const locString = guide.helpButtonLocation || 'floating';
    const parts = locString.split('|');
    const locationChoice = parts[0] || 'floating';
    const styleVal = parts[1] || 'icon-text';
    const orderVal = parts[2] || 'append';
    const sizeVal = parts[3] || 'large';
    const btnSizeVal = parts[4] || 'md';

    this.floatingButtonSize.set(sizeVal);

    if (locationChoice === 'floating') {
      this.showFloatingHelpButton.set(true);
      return;
    }

    // Resolve target selectors based on configuration
    let selectors: string[] = [];
    if (locationChoice === 'header') {
      selectors = [
        '[data-onboarding-anchor="project-header-actions"]',
        '[data-onboarding-anchor="header-actions"]',
        '[data-onboarding-anchor="actions-row"]',
        '.page-header-actions',
        '.min-w-0.flex-1',
        '.absolute.right-0.min-h-10',
        'div.flex.shrink-0.items-center.gap-3',
        'h2', 'h3', 'h4'
      ];
    } else if (locationChoice === 'navbar') {
      selectors = [
        '[data-onboarding-anchor="navbar-actions"]',
        '.nav-bar-actions',
        '.top-bar-actions',
        '.user-menu-button',
        'fuse-horizontal-navigation'
      ];
    } else if (locationChoice.startsWith('custom:')) {
      selectors = [locationChoice.substring(7)];
    }

    if (selectors.length === 0) {
      this.showFloatingHelpButton.set(true);
      return;
    }

    let retries = 0;
    const maxRetries = 30; // Check up to 3 seconds (100ms interval)

    const checkDom = () => {
      let targetElement: HTMLElement | null = null;
      let isHeadingEl = false;

      if (locationChoice === 'header') {
        const result = this.resolveHeaderTargetElement();
        targetElement = result.targetElement;
        isHeadingEl = result.isHeadingEl;
      } else {
        const result = this.resolveSelectorsTargetElement(selectors);
        targetElement = result.targetElement;
        isHeadingEl = result.isHeadingEl;
      }

      if (targetElement) {
        // Prevent duplicate injection
        const alreadyInjected = targetElement.querySelector('.onboarding-dynamic-help-btn') || 
                               (targetElement.parentElement && targetElement.parentElement.querySelector('.onboarding-dynamic-help-btn'));
        if (alreadyInjected) {
          return;
        }

        const btn = this.createHelpButton(guide, styleVal, orderVal, btnSizeVal);
        this.injectHelpButton(targetElement, btn, isHeadingEl, orderVal);

        this._injectedButton = btn;
        this.showFloatingHelpButton.set(false);

        // Keep watch of layout changes to re-inject if element re-renders
        this.setupMutationObserver(targetElement, guide);
      } else {
        retries++;
        if (retries < maxRetries) {
          this._retryTimer = setTimeout(checkDom, 100);
        } else {
          // Fallback to floating button
          this.showFloatingHelpButton.set(true);
        }
      }
    };

    checkDom();
  }

  private setupMutationObserver(targetElement: HTMLElement, guide: Onboarding): void {
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }

    this._mutationObserver = new MutationObserver(() => {
      // Re-inject if the button itself is no longer attached to the document body
      if (this._injectedButton && !document.body.contains(this._injectedButton)) {
        this.setupHelpButton(guide);
      }
    });

    // Observe document.body to catch removals anywhere in the current view hierarchy
    this._mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
}
